/**
 * Kitchen Builder — Module configuration UI for the Kitchen Estimator.
 * Handles layout, modules (base/wall/tall), countertop, backsplash,
 * appliances, sink, plumbing, and electrical selections.
 */
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChefHat, Plus, Trash2, Ruler, Utensils, Droplet, Zap, Sparkles, Wrench, Lightbulb, GripVertical, BookmarkPlus, Bookmark, X } from 'lucide-react';
import {
  KITCHEN_LAYOUTS, MODULE_CATEGORIES, BASE_UNITS, WALL_UNITS, TALL_UNITS,
  CARCASS_MATERIALS, SHUTTER_MATERIALS, DRAWER_SYSTEMS, TROLLEY_SYSTEMS,
  COUNTERTOP_MATERIALS, EDGE_PROFILES, BACKSPLASH_MATERIALS, APPLIANCES,
  SINK_TYPES, FAUCET_TYPES, ELECTRICAL_ITEMS,
  PLUMBING_SCOPES, PLUMBING_ADDONS, PLUMBING_MATERIALS_FLAT,
  getModuleDef,
} from './kitchenConfig';

// Smart Resize — module width constraints (mm)
const MIN_MODULE_WIDTH_MM = 300;
const MAX_MODULE_WIDTH_MM = 900;
const SMART_SUGGEST_THRESHOLD_MM = 150;  // 6 inches
const FT_TO_MM = 304.8;

export default function KitchenBuilder({ state, setState }) {
  const s = state;
  const update = (patch) => setState({ ...s, ...patch });
  const updateModule = (idx, patch) => {
    const next = [...s.modules];
    next[idx] = { ...next[idx], ...patch };
    update({ modules: next });
  };
  const removeModule = (idx) => update({ modules: s.modules.filter((_, i) => i !== idx) });
  const addModule = (category, overrides = {}) => {
    const defList = category === 'base' ? BASE_UNITS : category === 'wall' ? WALL_UNITS : TALL_UNITS;
    const def = defList[0];
    const mod = {
      id: Date.now() + Math.random(),
      category,
      type: def.id,
      widthMm: def.defaultWidthMm,
      heightMm: def.defaultHeightMm,
      depthMm: def.defaultDepthMm,
      qty: 1,
      carcass: s.defaults.carcass,
      shutter: s.defaults.shutter,
      drawerSystem: def.id === 'drawer' || def.id === 'tandem_stack' ? 'telescopic_sc' : 'none',
      drawerCount: def.defaultDrawers || 0,
      trolley: def.hasTrolley || 'none',
      ...overrides,
    };
    update({ modules: [...s.modules, mod] });
  };

  // === SMART RESIZE ENGINE ===
  // Whenever total running ft changes, re-distribute delta proportionally across
  // existing base modules (kept within 300-900mm). Leftover > 150mm fires a
  // "add-module" suggestion; if modules can't absorb a negative delta, fire a
  // "reduce / remove" suggestion.
  const [smartSuggest, setSmartSuggest] = useState(null); // { type: 'add'|'reduce'|'ok', message, sizeMm? }
  const prevRunningFtRef = useRef(s.totalRunningFt);
  const sumBaseWidthMm = (s.modules || []).filter(m => m.category === 'base').reduce((sum, m) => sum + (m.widthMm || 0) * (m.qty || 1), 0);

  useEffect(() => {
    const prev = prevRunningFtRef.current;
    const curr = s.totalRunningFt;
    if (prev === curr) return;
    prevRunningFtRef.current = curr;
    const baseModules = (s.modules || []).filter(m => m.category === 'base');
    if (baseModules.length === 0) { setSmartSuggest(null); return; }

    const targetMm = Math.round(curr * FT_TO_MM);
    const currentSumMm = baseModules.reduce((sum, m) => sum + (m.widthMm || 0) * (m.qty || 1), 0);
    let deltaMm = targetMm - currentSumMm;
    if (Math.abs(deltaMm) < 5) { setSmartSuggest(null); return; }

    // Distribute delta proportionally to each base module's share, clamped by min/max.
    const expanded = [];  // flatten qty into individual slots for easier re-distribution
    baseModules.forEach(m => {
      for (let i = 0; i < (m.qty || 1); i++) expanded.push({ moduleId: m.id, w: m.widthMm });
    });
    // Iteratively allocate delta: split evenly, clamp, iterate until leftover stabilises.
    let remaining = deltaMm;
    let guard = 0;
    while (Math.abs(remaining) >= 1 && guard < 8) {
      guard += 1;
      const share = remaining / expanded.filter(x => x.w + 0 !== (remaining > 0 ? MAX_MODULE_WIDTH_MM : MIN_MODULE_WIDTH_MM)).length || 1;
      let changed = 0;
      expanded.forEach(slot => {
        const want = slot.w + share;
        const clamped = Math.max(MIN_MODULE_WIDTH_MM, Math.min(MAX_MODULE_WIDTH_MM, want));
        const diff = clamped - slot.w;
        slot.w = clamped;
        changed += diff;
      });
      remaining -= changed;
      if (changed === 0) break;
    }

    // Rebuild modules array with redistributed widths. For qty>1 modules, use the average of their slots' widths.
    const nextModules = (s.modules || []).map(m => {
      if (m.category !== 'base') return m;
      const slots = expanded.filter(x => x.moduleId === m.id);
      if (slots.length === 0) return m;
      const avg = Math.round(slots.reduce((a, b) => a + b.w, 0) / slots.length);
      return { ...m, widthMm: avg };
    });
    setState({ ...s, modules: nextModules });

    // Build smart suggestion when leftover remains.
    if (Math.abs(remaining) >= SMART_SUGGEST_THRESHOLD_MM) {
      if (remaining > 0) {
        // Propose adding a standard-size module to absorb the extra length.
        const proposed = remaining >= 900 ? 900 : remaining >= 600 ? 600 : remaining >= 450 ? 450 : 300;
        setSmartSuggest({
          type: 'add',
          sizeMm: proposed,
          leftoverMm: Math.round(remaining),
          message: `Layout grew by ~${Math.round(remaining)}mm beyond what existing modules can absorb. Add a ${proposed}mm Base module?`,
        });
      } else {
        // Negative delta > threshold — suggest removing or shrinking.
        setSmartSuggest({
          type: 'reduce',
          leftoverMm: Math.round(Math.abs(remaining)),
          message: `Layout shrunk by ~${Math.round(Math.abs(remaining))}mm more than base modules can absorb. Consider removing the smallest module, or reducing a module below 300mm isn't allowed.`,
        });
      }
    } else {
      setSmartSuggest({ type: 'ok', message: `Modules auto-adjusted to match new layout (${Math.round(targetMm)}mm).` });
    }
  }, [s.totalRunningFt]);

  const acceptAddSuggestion = () => {
    if (!smartSuggest || smartSuggest.type !== 'add') return;
    addModule('base', { widthMm: smartSuggest.sizeMm });
    setSmartSuggest(null);
  };

  const removeSmallestBaseModule = () => {
    const baseMods = (s.modules || []).filter(m => m.category === 'base');
    if (baseMods.length === 0) return;
    const smallest = baseMods.reduce((a, b) => (a.widthMm < b.widthMm ? a : b));
    const idx = (s.modules || []).findIndex(m => m.id === smallest.id);
    if (idx >= 0) removeModule(idx);
    setSmartSuggest(null);
  };

  // iter-306: Rate Profiles — saved per-user reusable carcass/shutter rate presets.
  const [profiles, setProfiles] = useState([]);
  const [showSaveProfile, setShowSaveProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const profileAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/api/rate-profiles?scope=kitchen`, { headers: profileAuth() });
        setProfiles(Array.isArray(r.data) ? r.data : []);
      } catch {/* silent */}
    })();
  }, []);
  const applyProfile = (p) => {
    if (!p) return;
    const newDefaults = { ...s.defaults };
    if (p.carcass_id) newDefaults.carcass = p.carcass_id;
    if (p.carcass_rate != null) newDefaults.carcassRateOverride = +p.carcass_rate;
    if (p.shutter_id) newDefaults.shutter = p.shutter_id;
    if (p.shutter_rate != null) newDefaults.shutterRateOverride = +p.shutter_rate;
    const nextModules = s.modules.map(m => ({ ...m, carcass: newDefaults.carcass, shutter: newDefaults.shutter }));
    setState({ ...s, defaults: newDefaults, modules: nextModules });
    toast.success(`Applied profile "${p.name}"`);
  };
  const saveProfile = async () => {
    const name = (newProfileName || '').trim();
    if (!name) { toast.error('Enter a profile name'); return; }
    try {
      const body = {
        name, scope: 'kitchen',
        carcass_id: s.defaults.carcass,
        carcass_rate: s.defaults.carcassRateOverride,
        shutter_id: s.defaults.shutter,
        shutter_rate: s.defaults.shutterRateOverride,
      };
      const r = await axios.post(`${API_BASE}/api/rate-profiles`, body, { headers: profileAuth() });
      setProfiles(prev => {
        const filtered = prev.filter(p => p.id !== r.data.id);
        return [r.data, ...filtered];
      });
      setNewProfileName('');
      setShowSaveProfile(false);
      toast.success(`Profile "${name}" saved`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    }
  };
  const deleteProfile = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/rate-profiles/${id}`, { headers: profileAuth() });
      setProfiles(prev => prev.filter(p => p.id !== id));
    } catch {/* silent */}
  };

  // Propagate global default change to ALL existing modules (admin can still override per-module after).
  const setDefaultCarcass = (val) => {
    const nextModules = s.modules.map(m => ({ ...m, carcass: val }));
    setState({ ...s, defaults: { ...s.defaults, carcass: val }, modules: nextModules });
  };
  const setDefaultShutter = (val) => {
    const nextModules = s.modules.map(m => ({ ...m, shutter: val }));
    setState({ ...s, defaults: { ...s.defaults, shutter: val }, modules: nextModules });
  };
  const applyDefaultsToAll = () => {
    const nextModules = s.modules.map(m => ({ ...m, carcass: s.defaults.carcass, shutter: s.defaults.shutter }));
    update({ modules: nextModules });
  };

  // iter-289: Drag-to-reorder modules. Lets users group Base / Tall / Wall in
  // any preferred order before Cut List / CNC export.
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const moveModule = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...s.modules];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    update({ modules: next });
  };
  const handleRowDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    // Firefox needs setData to actually fire dragend
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch {}
  };
  const handleRowDragOver = (idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIdx !== idx) setDragOverIdx(idx);
  };
  const handleRowDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx !== null) moveModule(dragIdx, idx);
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleRowDragEnd = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const ModuleRowInner = (m, idx) => {
    const catList = MODULE_CATEGORIES[m.category] || [];
    const def = getModuleDef(m.category, m.type);
    const isDragging = dragIdx === idx;
    const isDropTarget = dragOverIdx === idx && dragIdx !== null && dragIdx !== idx;
    return (
      <tr
        key={m.id}
        className={`border-b hover:bg-stone-50 transition-colors ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'bg-blue-50 border-t-2 border-t-blue-400' : ''}`}
        data-testid={`kitchen-module-row-${idx}`}
        draggable
        onDragStart={handleRowDragStart(idx)}
        onDragOver={handleRowDragOver(idx)}
        onDrop={handleRowDrop(idx)}
        onDragEnd={handleRowDragEnd}
      >
        <td className="px-1 py-1.5 text-stone-300 hover:text-stone-600 cursor-grab active:cursor-grabbing select-none w-6" title="Drag to reorder" data-testid={`kitchen-mod-drag-${idx}`}>
          <GripVertical size={14} />
        </td>
        <td className="px-2 py-1.5">
          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${m.category === 'base' ? 'bg-blue-100 text-blue-700' : m.category === 'wall' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{m.category}</span>
        </td>
        <td className="px-2 py-1.5">
          <select value={m.type} onChange={e => {
            const newDef = catList.find(x => x.id === e.target.value);
            updateModule(idx, {
              type: e.target.value,
              widthMm: newDef?.defaultWidthMm || m.widthMm,
              heightMm: newDef?.defaultHeightMm || m.heightMm,
              depthMm: newDef?.defaultDepthMm || m.depthMm,
              drawerCount: newDef?.defaultDrawers ?? m.drawerCount,
              trolley: newDef?.hasTrolley || 'none',
            });
          }} className="h-8 text-xs rounded border border-stone-300 px-1.5 w-full max-w-[160px]" data-testid={`kitchen-mod-type-${idx}`}>
            {catList.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </td>
        <td className="px-1 py-1.5"><Input className="h-8 text-xs w-20 no-spinner" type="number" step="10" min="0" value={m.widthMm} onChange={e => updateModule(idx, { widthMm: +e.target.value })} /></td>
        <td className="px-1 py-1.5"><Input className="h-8 text-xs w-20 no-spinner" type="number" step="10" min="0" value={m.heightMm} onChange={e => updateModule(idx, { heightMm: +e.target.value })} /></td>
        <td className="px-1 py-1.5"><Input className="h-8 text-xs w-20 no-spinner" type="number" step="10" min="0" value={m.depthMm} onChange={e => updateModule(idx, { depthMm: +e.target.value })} /></td>
        <td className="px-1 py-1.5"><Input className="h-8 text-xs w-14 no-spinner" type="number" step="1" min="1" value={m.qty} onChange={e => updateModule(idx, { qty: Math.max(1, +e.target.value) })} /></td>
        <td className="px-2 py-1.5">
          <select value={m.carcass} onChange={e => updateModule(idx, { carcass: e.target.value })} className="h-8 text-xs rounded border border-stone-300 px-1 w-24">
            {CARCASS_MATERIALS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </td>
        <td className="px-2 py-1.5">
          <select value={m.shutter || 'none'} onChange={e => updateModule(idx, { shutter: e.target.value })} className="h-8 text-xs rounded border border-stone-300 px-1 w-28">
            <option value="none">None</option>
            {SHUTTER_MATERIALS.map(sh => <option key={sh.id} value={sh.id}>{sh.label}</option>)}
          </select>
        </td>
        <td className="px-2 py-1.5">
          <select value={m.drawerSystem} onChange={e => updateModule(idx, { drawerSystem: e.target.value })} className="h-8 text-xs rounded border border-stone-300 px-1 w-28">
            {DRAWER_SYSTEMS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </select>
        </td>
        <td className="px-1 py-1.5"><Input className="h-8 text-xs w-14 no-spinner" type="number" step="1" min="0" value={m.drawerCount} onChange={e => updateModule(idx, { drawerCount: Math.max(0, +e.target.value) })} /></td>
        <td className="px-2 py-1.5">
          <select value={m.trolley} onChange={e => updateModule(idx, { trolley: e.target.value })} className="h-8 text-xs rounded border border-stone-300 px-1 w-28">
            <option value="none">None</option>
            {TROLLEY_SYSTEMS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </td>
        <td className="px-2 py-1.5 text-right">
          <button onClick={() => removeModule(idx)} className="text-red-500 hover:text-red-700" data-testid={`kitchen-mod-remove-${idx}`}><Trash2 size={14} /></button>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-5" data-testid="kitchen-builder">
      {/* === LAYOUT === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><ChefHat size={18} className="text-emerald-600" /><h3 className="font-bold text-stone-800">Kitchen Layout</h3></div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
            {KITCHEN_LAYOUTS.map(l => (
              <button key={l.id} onClick={() => update({ layout: l.id })}
                className={`flex flex-col items-center gap-1 px-2 py-3 rounded-lg border text-xs transition-colors ${s.layout === l.id ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'}`}
                data-testid={`kitchen-layout-${l.id}`}>
                <span className="text-xl">{l.icon}</span>
                <span className="font-medium">{l.label}</span>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label>Total Running ft</Label><Input type="number" value={s.totalRunningFt} onChange={e => update({ totalRunningFt: +e.target.value })} data-testid="kitchen-running-ft" /></div>
            <div><Label>Ceiling Height (ft)</Label><Input type="number" value={s.ceilingHeight} onChange={e => update({ ceilingHeight: +e.target.value })} /></div>
            <div><Label>Wall 1 Length (ft)</Label><Input type="number" value={s.wall1} onChange={e => update({ wall1: +e.target.value })} /></div>
            <div><Label>Wall 2 Length (ft)</Label><Input type="number" value={s.wall2} onChange={e => update({ wall2: +e.target.value })} /></div>
          </div>
          {(() => {
            const targetMm = Math.round((s.totalRunningFt || 0) * FT_TO_MM);
            const matchDiff = Math.round(targetMm - sumBaseWidthMm);
            const matched = Math.abs(matchDiff) < 5;
            return (
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs" data-testid="kitchen-layout-vs-modules">
                <span className="text-stone-500">Target: <b className="text-stone-800">{targetMm}mm</b></span>
                <span className="text-stone-400">·</span>
                <span className="text-stone-500">Base modules sum: <b className={matched ? 'text-emerald-700' : Math.abs(matchDiff) < SMART_SUGGEST_THRESHOLD_MM ? 'text-amber-700' : 'text-red-600'}>{Math.round(sumBaseWidthMm)}mm</b></span>
                {!matched && <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-semibold ${Math.abs(matchDiff) < SMART_SUGGEST_THRESHOLD_MM ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`}>{matchDiff > 0 ? '+' : ''}{matchDiff}mm</span>}
              </div>
            );
          })()}
          {smartSuggest && (
            <div className={`mt-3 p-3 rounded-lg border flex items-start gap-3 ${smartSuggest.type === 'add' ? 'bg-blue-50 border-blue-200' : smartSuggest.type === 'reduce' ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="kitchen-smart-suggest-banner">
              <Lightbulb size={18} className={smartSuggest.type === 'add' ? 'text-blue-600' : smartSuggest.type === 'reduce' ? 'text-red-600' : 'text-emerald-600'} />
              <div className="flex-1">
                <p className={`text-sm font-semibold ${smartSuggest.type === 'add' ? 'text-blue-900' : smartSuggest.type === 'reduce' ? 'text-red-900' : 'text-emerald-900'}`}>Smart Suggestion</p>
                <p className="text-xs text-stone-700 mt-0.5">{smartSuggest.message}</p>
                <div className="flex gap-2 mt-2">
                  {smartSuggest.type === 'add' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-blue-400 text-blue-700 hover:bg-blue-100" onClick={acceptAddSuggestion} data-testid="kitchen-smart-suggest-accept">
                      <Plus size={12} className="mr-1" />Add {smartSuggest.sizeMm}mm Base Module
                    </Button>
                  )}
                  {smartSuggest.type === 'reduce' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-400 text-red-700 hover:bg-red-100" onClick={removeSmallestBaseModule} data-testid="kitchen-smart-suggest-reduce">
                      <Trash2 size={12} className="mr-1" />Remove Smallest Base Module
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-stone-500" onClick={() => setSmartSuggest(null)} data-testid="kitchen-smart-suggest-dismiss">Dismiss</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === GLOBAL DEFAULTS === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Sparkles size={18} className="text-purple-600" /><h3 className="font-bold text-stone-800">Global Defaults</h3>
              <span className="text-xs text-stone-500">(applied to every module — per-module override available below)</span>
            </div>
            <Button size="sm" variant="outline" onClick={applyDefaultsToAll} data-testid="apply-defaults-to-all-btn">
              <Sparkles size={12} className="mr-1" />Re-apply to all modules
            </Button>
          </div>

          {/* iter-306: Saved Rate Profiles */}
          <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50/40 p-3" data-testid="rate-profiles-panel">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Bookmark size={14} className="text-purple-700" />
              <span className="text-xs font-semibold uppercase tracking-wide text-purple-800">Saved Rate Profiles</span>
              <span className="text-[11px] text-purple-600">Apply or save material rate presets in one click</span>
              <Button size="sm" variant="outline" className="ml-auto h-7 text-xs border-purple-300 text-purple-800 hover:bg-purple-100" onClick={() => setShowSaveProfile(v => !v)} data-testid="rate-profile-save-toggle">
                <BookmarkPlus size={12} className="mr-1" />{showSaveProfile ? 'Cancel' : 'Save current as Profile'}
              </Button>
            </div>
            {showSaveProfile && (
              <div className="flex items-center gap-2 mb-2">
                <Input
                  placeholder='e.g., "Premium BWP @ Rs 110"'
                  value={newProfileName}
                  onChange={e => setNewProfileName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveProfile(); }}
                  className="h-8 text-sm"
                  data-testid="rate-profile-name-input"
                />
                <Button size="sm" className="h-8 bg-purple-600 hover:bg-purple-700 text-white" onClick={saveProfile} data-testid="rate-profile-save-confirm">Save</Button>
              </div>
            )}
            {profiles.length === 0 ? (
              <p className="text-[11px] text-purple-600 italic">No profiles saved yet — set your Carcass / Shutter rates below then click "Save current as Profile".</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="rate-profile-chips">
                {profiles.map(p => (
                  <div key={p.id} className="group inline-flex items-center gap-1 bg-white border border-purple-300 text-purple-800 rounded-full px-2 py-1 text-xs hover:bg-purple-100 transition" data-testid={`rate-profile-chip-${p.id}`}>
                    <button type="button" onClick={() => applyProfile(p)} className="font-medium" data-testid={`rate-profile-apply-${p.id}`}>
                      {p.name}
                    </button>
                    <button type="button" onClick={() => deleteProfile(p.id)} className="text-purple-400 hover:text-red-600 ml-1" title="Delete profile" data-testid={`rate-profile-delete-${p.id}`}>
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-stone-200 p-3 bg-stone-50/40">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Default Carcass</Label>
                <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer" data-testid="default-carcass-include-label">
                  <input
                    type="checkbox"
                    checked={s.defaults.carcassIncluded !== false}
                    onChange={e => setState({ ...s, defaults: { ...s.defaults, carcassIncluded: e.target.checked } })}
                    data-testid="default-carcass-include"
                  />
                  Include in costing
                </label>
              </div>
              <select
                value={s.defaults.carcass}
                onChange={e => setDefaultCarcass(e.target.value)}
                disabled={s.defaults.carcassIncluded === false}
                className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm disabled:opacity-50 disabled:bg-stone-100"
                data-testid="default-carcass-select"
              >
                {CARCASS_MATERIALS.map(c => <option key={c.id} value={c.id}>{c.label} (Rs {c.rate}/sqft)</option>)}
              </select>
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-[11px] text-stone-500 whitespace-nowrap">Override Rate</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="(default)"
                  value={s.defaults.carcassRateOverride ?? ''}
                  onChange={e => setState({ ...s, defaults: { ...s.defaults, carcassRateOverride: e.target.value === '' ? null : +e.target.value } })}
                  disabled={s.defaults.carcassIncluded === false}
                  className="h-8 text-sm disabled:opacity-50 disabled:bg-stone-100"
                  data-testid="default-carcass-rate-override"
                />
                <span className="text-[11px] text-stone-500">Rs/sqft</span>
              </div>
            </div>
            <div className="rounded-lg border border-stone-200 p-3 bg-stone-50/40">
              <div className="flex items-center justify-between mb-2">
                <Label className="font-semibold">Default Shutter</Label>
                <label className="flex items-center gap-1.5 text-xs text-stone-600 cursor-pointer" data-testid="default-shutter-include-label">
                  <input
                    type="checkbox"
                    checked={s.defaults.shutterIncluded !== false}
                    onChange={e => setState({ ...s, defaults: { ...s.defaults, shutterIncluded: e.target.checked } })}
                    data-testid="default-shutter-include"
                  />
                  Include in costing
                </label>
              </div>
              <select
                value={s.defaults.shutter}
                onChange={e => setDefaultShutter(e.target.value)}
                disabled={s.defaults.shutterIncluded === false}
                className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm disabled:opacity-50 disabled:bg-stone-100"
                data-testid="default-shutter-select"
              >
                {SHUTTER_MATERIALS.map(sh => <option key={sh.id} value={sh.id}>{sh.label} (Rs {sh.rate}/sqft)</option>)}
              </select>
              <div className="mt-2 flex items-center gap-2">
                <Label className="text-[11px] text-stone-500 whitespace-nowrap">Override Rate</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="(default)"
                  value={s.defaults.shutterRateOverride ?? ''}
                  onChange={e => setState({ ...s, defaults: { ...s.defaults, shutterRateOverride: e.target.value === '' ? null : +e.target.value } })}
                  disabled={s.defaults.shutterIncluded === false}
                  className="h-8 text-sm disabled:opacity-50 disabled:bg-stone-100"
                  data-testid="default-shutter-rate-override"
                />
                <span className="text-[11px] text-stone-500">Rs/sqft</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* === MODULE BUILDER === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Ruler size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">Modules</h3>
              <span className="text-xs bg-stone-100 px-2 py-0.5 rounded-full text-stone-600">{s.modules.length} modules</span>
              <span className="text-[11px] text-stone-400 hidden sm:inline-flex items-center gap-1" data-testid="kitchen-modules-reorder-hint">
                <GripVertical size={11} /> Drag rows to reorder
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => addModule('base')} data-testid="add-base-unit"><Plus size={14} className="mr-1" />Base</Button>
              <Button size="sm" variant="outline" onClick={() => addModule('wall')} data-testid="add-wall-unit"><Plus size={14} className="mr-1" />Wall</Button>
              <Button size="sm" variant="outline" onClick={() => addModule('tall')} data-testid="add-tall-unit"><Plus size={14} className="mr-1" />Tall</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="kitchen-modules-table">
              <thead className="bg-stone-50 border-b">
                <tr className="text-xs text-stone-600">
                  <th className="px-1 py-2 w-6"></th>
                  <th className="px-2 py-2 text-left font-semibold">Cat</th>
                  <th className="px-2 py-2 text-left font-semibold">Type</th>
                  <th className="px-1 py-2 text-left font-semibold">W(mm)</th>
                  <th className="px-1 py-2 text-left font-semibold">H(mm)</th>
                  <th className="px-1 py-2 text-left font-semibold">D(mm)</th>
                  <th className="px-1 py-2 text-left font-semibold">Qty</th>
                  <th className="px-2 py-2 text-left font-semibold">Carcass</th>
                  <th className="px-2 py-2 text-left font-semibold">Shutter / Facia</th>
                  <th className="px-2 py-2 text-left font-semibold">Drawer Sys</th>
                  <th className="px-1 py-2 text-left font-semibold">#Dr</th>
                  <th className="px-2 py-2 text-left font-semibold">Trolley</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {s.modules.map((m, i) => ModuleRowInner(m, i))}
                {s.modules.length === 0 && <tr><td colSpan={13} className="text-center py-6 text-stone-400 text-sm">No modules yet. Click Base / Wall / Tall to add.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* === DRAWER SYSTEM & TROLLEY RATE CARD (editable) === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Wrench size={18} className="text-indigo-600" /><h3 className="font-bold text-stone-800">Drawer System & Trolley Rates</h3><span className="text-xs text-stone-500">(admin-editable — applied per module selection)</span></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-stone-600 mb-2">Drawer Channels / Tandems (Rs per drawer)</p>
              <div className="space-y-1.5">
                {DRAWER_SYSTEMS.filter(d => d.id !== 'none').map(d => (
                  <div key={d.id} className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-7 text-xs text-stone-700">{d.label}</span>
                    <Input type="number" value={s.drawerRates?.[d.id] ?? d.rate} onChange={e => update({ drawerRates: { ...(s.drawerRates || {}), [d.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-4 text-xs" data-testid={`drawer-rate-${d.id}`} />
                    <span className="col-span-1 text-[10px] text-stone-400">Rs</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-stone-600 mb-2">Trolley / Accessories (Rs per unit)</p>
              <div className="space-y-1.5">
                {TROLLEY_SYSTEMS.map(t => (
                  <div key={t.id} className="grid grid-cols-12 gap-2 items-center">
                    <span className="col-span-7 text-xs text-stone-700">{t.label}</span>
                    <Input type="number" value={s.trolleyRates?.[t.id] ?? t.rate} onChange={e => update({ trolleyRates: { ...(s.trolleyRates || {}), [t.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-4 text-xs" data-testid={`trolley-rate-${t.id}`} />
                    <span className="col-span-1 text-[10px] text-stone-400">Rs</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-stone-500 mt-3">Rates apply to selections made in the Drawer Sys / Trolley columns of each module above. Counts are auto-rolled into Hardware Cost.</p>
        </CardContent>
      </Card>

      {/* === COUNTERTOP === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Utensils size={18} className="text-amber-600" /><h3 className="font-bold text-stone-800">Countertop</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Material</Label>
              <select value={s.countertop.material} onChange={e => update({ countertop: { ...s.countertop, material: e.target.value } })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="countertop-material">
                {COUNTERTOP_MATERIALS.map(c => <option key={c.id} value={c.id}>{c.label}{c.isCustom ? '' : ` (Rs ${c.rate}/sqft)`}</option>)}
              </select>
            </div>
            <div><Label>Running ft</Label><Input type="number" value={s.countertop.runningFt} onChange={e => update({ countertop: { ...s.countertop, runningFt: +e.target.value } })} /></div>
            <div><Label>Thickness (mm)</Label><Input type="number" value={s.countertop.thicknessMm} onChange={e => update({ countertop: { ...s.countertop, thicknessMm: +e.target.value } })} /></div>
            <div>
              <Label>Edge Profile</Label>
              <select value={s.countertop.edge} onChange={e => update({ countertop: { ...s.countertop, edge: e.target.value } })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm">
                {EDGE_PROFILES.map(ed => <option key={ed.id} value={ed.id}>{ed.label}{ed.rate ? ` (+Rs ${ed.rate}/ft)` : ''}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={s.countertop.sinkCutout} onChange={e => update({ countertop: { ...s.countertop, sinkCutout: e.target.checked } })} /><span className="text-sm">Sink cutout (+Rs 1500)</span></div>
            <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={s.countertop.hobCutout} onChange={e => update({ countertop: { ...s.countertop, hobCutout: e.target.checked } })} /><span className="text-sm">Hob cutout (+Rs 1200)</span></div>
          </div>
          {s.countertop.material === 'custom' && (
            <div className="mt-3 p-3 rounded-lg border border-dashed border-amber-300 bg-amber-50/40" data-testid="countertop-custom-panel">
              <p className="text-xs font-semibold text-amber-800 mb-2">Custom Material Details</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Material Name</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Italian Carrara Marble"
                    value={s.countertop.customName || ''}
                    onChange={e => update({ countertop: { ...s.countertop, customName: e.target.value } })}
                    data-testid="countertop-custom-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Rate (Rs / sqft)</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="e.g. 650"
                    value={s.countertop.customRate ?? ''}
                    onChange={e => update({ countertop: { ...s.countertop, customRate: Math.max(0, +e.target.value) } })}
                    data-testid="countertop-custom-rate"
                  />
                </div>
              </div>
              <p className="text-[11px] text-amber-700 mt-2">This name &amp; rate will appear on the Client Estimate PDF &amp; BOQ.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* === BACKSPLASH === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><span className="text-orange-600">▦</span><h3 className="font-bold text-stone-800">Backsplash</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div>
              <Label>Material</Label>
              <select value={s.backsplash.material} onChange={e => update({ backsplash: { ...s.backsplash, material: e.target.value } })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="backsplash-material">
                {BACKSPLASH_MATERIALS.map(b => <option key={b.id} value={b.id}>{b.label}{b.rate ? ` (Rs ${b.rate}/sqft)` : ''}</option>)}
              </select>
            </div>
            <div><Label>Running ft</Label><Input type="number" value={s.backsplash.runningFt} onChange={e => update({ backsplash: { ...s.backsplash, runningFt: +e.target.value } })} /></div>
            <div><Label>Height (ft)</Label><Input type="number" step="0.1" value={s.backsplash.heightFt} onChange={e => update({ backsplash: { ...s.backsplash, heightFt: +e.target.value } })} /></div>
          </div>
        </CardContent>
      </Card>

      {/* === APPLIANCES === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><span className="text-red-500">▲</span><h3 className="font-bold text-stone-800">Appliances</h3></div>
            <div className="text-xs text-stone-500">
              <label className="mr-3"><input type="radio" checked={s.appliancesMode === 'allowance'} onChange={() => update({ appliancesMode: 'allowance' })} /> Allowance</label>
              <label><input type="radio" checked={s.appliancesMode === 'client'} onChange={() => update({ appliancesMode: 'client' })} /> Client-Supplied (0 cost)</label>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
            {APPLIANCES.map(a => (
              <label key={a.id} className="flex items-center gap-2 px-3 py-2 rounded border border-stone-200 hover:bg-stone-50 cursor-pointer">
                <input type="checkbox" checked={!!s.appliances[a.id]} onChange={e => update({ appliances: { ...s.appliances, [a.id]: e.target.checked ? a.defaultPrice : 0 } })} />
                <div className="flex-1">
                  <p className="font-medium text-xs">{a.label}</p>
                  {s.appliances[a.id] > 0 && s.appliancesMode === 'allowance' && (
                    <input type="number" value={s.appliances[a.id]} onChange={e => update({ appliances: { ...s.appliances, [a.id]: +e.target.value } })} className="h-6 w-24 text-xs rounded border border-stone-300 px-1.5 mt-0.5" onClick={e => e.stopPropagation()} />
                  )}
                </div>
              </label>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* === SINK === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Droplet size={18} className="text-cyan-600" /><h3 className="font-bold text-stone-800">Sink & Faucet</h3></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <Label>Sink Type</Label>
              <select value={s.sink.type} onChange={e => update({ sink: { ...s.sink, type: e.target.value } })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="sink-type-select">
                <option value="none">None</option>
                {SINK_TYPES.map(sk => <option key={sk.id} value={sk.id}>{sk.label} (Rs {sk.rate.toLocaleString()})</option>)}
              </select>
            </div>
            <div>
              <Label>Faucet</Label>
              <select value={s.sink.faucet} onChange={e => update({ sink: { ...s.sink, faucet: e.target.value } })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="faucet-type-select">
                <option value="none">None</option>
                {FAUCET_TYPES.map(ft => <option key={ft.id} value={ft.id}>{ft.label} (Rs {ft.rate.toLocaleString()})</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2 pt-6"><input type="checkbox" checked={s.sink.roCutout} onChange={e => update({ sink: { ...s.sink, roCutout: e.target.checked } })} /><span className="text-sm">RO Cutout (+Rs 400)</span></div>
          </div>
        </CardContent>
      </Card>

      {/* === PLUMBING COSTING ENGINE === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Wrench size={18} className="text-blue-600" /><h3 className="font-bold text-stone-800">Plumbing Costing Engine</h3><span className="text-xs text-stone-500">(admin-editable rates, smart auto-suggestion)</span></div>
          {(() => {
            const p = s.plumbing || { scope: 'standard', billingMode: 'lump', addons: {}, includeMaterial: false, customRate: 0, customLabel: '' };
            const setP = (patch) => update({ plumbing: { ...p, ...patch } });
            // Smart suggestion: if certain modules present → hint
            const hasSink = s.modules.some(m => m.type === 'sink');
            const hasDishwasher = s.modules.some(m => m.type === 'dishwasher');
            const hasUtility = s.modules.some(m => m.type === 'utility_base' || m.type === 'utility_tall');
            const suggestions = [];
            if (hasSink) suggestions.push('Sink Install + Faucet Install');
            if (hasDishwasher) suggestions.push('Dishwasher Point');
            if (hasUtility) suggestions.push('Washing Machine Point');
            return (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <Label>Plumbing Scope</Label>
                    <select value={p.scope} onChange={e => setP({ scope: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="plumbing-scope-select">
                      {PLUMBING_SCOPES.map(sc => <option key={sc.id} value={sc.id}>{sc.label}{sc.baseRate ? ` — Rs ${sc.baseRate.toLocaleString('en-IN')}` : ''}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label>Billing Mode</Label>
                    <select value={p.billingMode} onChange={e => setP({ billingMode: e.target.value })} className="h-9 w-full rounded-md border border-stone-300 px-2 text-sm" data-testid="plumbing-billing-mode">
                      <option value="lump">Lump Sum (scope rate only)</option>
                      <option value="itemwise">Item-wise (per add-on)</option>
                    </select>
                  </div>
                  <div>
                    <Label>Scope Base Rate (editable)</Label>
                    <Input type="number" value={p.scope === 'custom' ? (+p.customRate || 0) : (s.plumbingRates?.[p.scope] ?? (PLUMBING_SCOPES.find(x => x.id === p.scope)?.baseRate ?? 0))}
                      onChange={e => {
                        if (p.scope === 'custom') setP({ customRate: Math.max(0, +e.target.value) });
                        else update({ plumbingRates: { ...(s.plumbingRates || {}), [p.scope]: Math.max(0, +e.target.value) } });
                      }}
                      data-testid="plumbing-scope-rate" />
                  </div>
                </div>

                {p.billingMode === 'itemwise' && (
                  <div className="border-t pt-3 mt-2">
                    <p className="text-xs font-semibold text-stone-600 mb-2">Add-on Items (qty × rate)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {PLUMBING_ADDONS.map(a => (
                        <div key={a.id} className="grid grid-cols-12 gap-2 items-center">
                          <span className="col-span-5 text-xs text-stone-700">{a.label}</span>
                          <Input type="number" value={p.addons?.[a.id] || 0} onChange={e => setP({ addons: { ...(p.addons || {}), [a.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-2 text-xs" data-testid={`plumbing-addon-qty-${a.id}`} />
                          <span className="col-span-1 text-[10px] text-stone-400">qty</span>
                          <Input type="number" value={s.plumbingAddonRates?.[a.id] ?? a.rate} onChange={e => update({ plumbingAddonRates: { ...(s.plumbingAddonRates || {}), [a.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-3 text-xs" data-testid={`plumbing-addon-rate-${a.id}`} />
                          <span className="col-span-1 text-[10px] text-stone-400">Rs</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-4 mt-3 border-t pt-3">
                  <label className="flex items-center gap-2 text-sm text-stone-700">
                    <input type="checkbox" checked={!!p.includeMaterial} onChange={e => setP({ includeMaterial: e.target.checked })} data-testid="plumbing-include-material" />
                    Include plumbing materials (CPVC/UPVC/valves/hoses)
                  </label>
                  {p.includeMaterial && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Flat Rate (Rs)</Label>
                      <Input type="number" className="h-8 w-28" value={s.plumbingMaterialFlat ?? PLUMBING_MATERIALS_FLAT} onChange={e => update({ plumbingMaterialFlat: Math.max(0, +e.target.value) })} data-testid="plumbing-material-flat" />
                    </div>
                  )}
                </div>

                {suggestions.length > 0 && (
                  <div className="mt-3 p-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800" data-testid="plumbing-suggestions">
                    <span className="font-semibold">Smart suggestion: </span>{suggestions.join(' · ')} → consider including in the scope/add-ons above.
                  </div>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      {/* === ELECTRICAL === */}
      <Card>
        <CardContent className="pt-5 pb-4">
          <div className="flex items-center gap-2 mb-3"><Zap size={18} className="text-yellow-600" /><h3 className="font-bold text-stone-800">Electrical</h3><span className="text-xs text-stone-500">(rates are admin-editable)</span></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            {ELECTRICAL_ITEMS.map(el => (
              <div key={el.id} className="grid grid-cols-12 gap-2 items-center">
                <span className="col-span-5 text-xs text-stone-700">{el.label}</span>
                <Input type="number" value={s.electrical[el.id] || 0} onChange={e => update({ electrical: { ...s.electrical, [el.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-2 text-xs" />
                <span className="col-span-1 text-[10px] text-stone-400">qty</span>
                <Input type="number" value={s.electricalRates?.[el.id] ?? el.rate} onChange={e => update({ electricalRates: { ...(s.electricalRates || {}), [el.id]: Math.max(0, +e.target.value) } })} className="h-8 col-span-3 text-xs" />
                <span className="col-span-1 text-[10px] text-stone-400">Rs/{el.unit === 'each' ? 'ea' : 'ft'}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
